import { useState } from "react";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Section } from "@/components/editor/Section";
import { AssetList } from "@/components/editor/AssetList";
import { VFX_CATEGORIES, VFX_LIBRARY, CGI_CATEGORIES, CGI_LIBRARY } from "@/lib/vfxLibrary";
import { api, formatApiError } from "@/lib/api";

export const VfxPanel = ({ projectId, assets, onCreated, onAdd, cgi = false }) => {
  const categories = cgi ? CGI_CATEGORIES : VFX_CATEGORIES;
  const library = cgi ? CGI_LIBRARY : VFX_LIBRARY;
  const [cat, setCat] = useState(categories[0].id);
  const [picked, setPicked] = useState(null);
  const [name, setName] = useState("");
  const [prompt, setPrompt] = useState("");
  const [sound, setSound] = useState(true);
  const [soundPrompt, setSoundPrompt] = useState("");
  const [duration, setDuration] = useState("5");
  const [busy, setBusy] = useState(false);
  const color = categories.find((c) => c.id === cat).color;
  const tid = cgi ? "cgi" : "vfx";

  const pick = (p) => { setPicked(p.id); setName(p.name); setPrompt(p.prompt); setSoundPrompt(p.sound); };

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/generate/vfx", { project_id: projectId, name: name || "Custom effect", prompt, duration, background: cgi ? "green" : "black", sound_prompt: sound ? soundPrompt || prompt : null });
      onCreated(data.vfx);
      if (data.sfx) onCreated(data.sfx);
      toast.success(`Rendering "${data.vfx.name}"${data.sfx ? " + matching sound" : ""} — usually 1-3 minutes`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid={`${tid}-panel`}>
      <Section title={cgi ? "CGI elements library" : "Special effects library"}>
        <div className="flex flex-wrap gap-1 mb-3">
          {categories.map((c) => (
            <button key={c.id} onClick={() => setCat(c.id)} data-testid={`${tid}-category-${c.id}`}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${cat === c.id ? "text-[#0B0C10] font-semibold" : "border-[#2A2E3D] text-slate-300 hover:text-white"}`}
              style={cat === c.id ? { background: c.color, borderColor: c.color } : {}}>{c.name}</button>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-2">
          {library.filter((p) => p.category === cat).map((p) => (
            <button key={p.id} onClick={() => pick(p)} data-testid={`${tid}-preset-${p.id}`}
              className={`text-left p-2.5 rounded-md bg-[#0B0C10] border transition-colors ${picked === p.id ? "" : "border-[#2A2E3D] hover:border-[#3F455A]"}`}
              style={picked === p.id ? { borderColor: color, boxShadow: `0 0 14px ${color}33` } : {}}>
              <Sparkles className="w-3.5 h-3.5 mb-1.5" style={{ color }} />
              <p className="text-[11px] font-medium leading-tight">{p.name}</p>
            </button>
          ))}
        </div>
      </Section>
      <Section title="Customise">
        <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Effect name" className="bg-[#0B0C10] h-9 mb-2" data-testid={`${tid}-name-input`} />
        <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Pick a preset or describe any effect, e.g. green acid spit splash" className="bg-[#0B0C10] text-sm min-h-[72px]" data-testid={`${tid}-prompt-input`} />
        <div className="flex items-center justify-between mt-3 text-xs text-slate-300"><span>Length</span>
          <div className="flex gap-1">{["5", "10"].map((d) => (
            <button key={d} onClick={() => setDuration(d)} data-testid={`${tid}-duration-${d}`}
              className={`px-2.5 py-1 rounded text-[11px] ${duration === d ? "bg-[#EC4899] text-white" : "bg-[#0B0C10] text-slate-400"}`}>{d} s</button>))}
          </div>
        </div>
        <div className="flex items-center justify-between mt-3 text-xs text-slate-300"><span>Add matching sound effect</span><Switch checked={sound} onCheckedChange={setSound} data-testid={`${tid}-sound-switch`} /></div>
        {sound && <Input value={soundPrompt} onChange={(e) => setSoundPrompt(e.target.value)} placeholder="Sound description" className="bg-[#0B0C10] h-9 mt-2" data-testid={`${tid}-sound-input`} />}
        <Button onClick={generate} disabled={busy || prompt.trim().length < 2} className="w-full mt-3 bg-[#EC4899] hover:bg-[#f472b6] text-white" data-testid={`${tid}-generate-button`}>
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}Generate effect
        </Button>
        <p className="text-[11px] text-slate-500 mt-2">{cgi ? "CGI elements render on a green screen and are chroma-keyed automatically on the V2 layer track. Select the clip to move, scale, rotate and fine-tune the key." : "Effects render on pure black and are screen-blended over your picture on the V2 layer track. Select the clip to set opacity, fades and position."}</p>
      </Section>
      <Section title={cgi ? "Your CGI elements" : "Your effects"}>
        <AssetList assets={assets.filter((a) => a.kind === "vfx" && !a.meta?.light && !!a.meta?.cgi === cgi)} empty="Generated elements appear here." onAdd={onAdd} />
      </Section>
    </div>
  );
};
